import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Car } from '@/types';
import { getFavorites, getFavoriteIds, addFavorite, removeFavorite } from '@/lib/api';

interface FavoriteState {
  favoriteIds: Set<string>;
  favoriteCars: Car[];
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchFavorites: () => Promise<void>;
  fetchFavoriteIds: () => Promise<void>;
  toggleFavorite: (carId: string) => Promise<void>;
  isFavorite: (carId: string) => boolean;
  reset: () => void;
}

export const useFavoriteStore = create<FavoriteState>()(
  persist(
    (set, get) => ({
      favoriteIds: new Set<string>(),
      favoriteCars: [],
      isLoading: false,
      error: null,

      fetchFavorites: async () => {
        set({ isLoading: true, error: null });

        try {
          const response = await getFavorites();
          const cars = response.data || [];
          set({
            favoriteCars: cars,
            favoriteIds: new Set(cars.map((car) => car.id)),
            isLoading: false,
          });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch favorites',
            isLoading: false,
          });
        }
      },

      fetchFavoriteIds: async () => {
        try {
          const response = await getFavoriteIds();
          const ids = response.data || [];
          set({ favoriteIds: new Set(ids) });
        } catch (error) {
          console.error('Failed to fetch favorite IDs:', error);
        }
      },

      toggleFavorite: async (carId: string) => {
        const { favoriteIds } = get();
        const isFav = favoriteIds.has(carId);

        // Optimistic update
        const newIds = new Set(favoriteIds);
        if (isFav) {
          newIds.delete(carId);
        } else {
          newIds.add(carId);
        }
        set({ favoriteIds: newIds });

        try {
          if (isFav) {
            await removeFavorite(carId);
          } else {
            await addFavorite(carId);
          }
        } catch (error) {
          // Revert on error
          set({ favoriteIds });
          console.error('Failed to toggle favorite:', error);
        }
      },

      isFavorite: (carId: string) => {
        return get().favoriteIds.has(carId);
      },

      // Clear all favorite state (in-memory + persisted) — called on logout so one
      // user's favorites never carry over to the next session on a shared device.
      reset: () => {
        set({ favoriteIds: new Set<string>(), favoriteCars: [], isLoading: false, error: null });
      },
    }),
    {
      name: 'kurdacars-favorites',
      partialize: (state) => ({
        favoriteIds: Array.from(state.favoriteIds),
      }),
      merge: (persisted, current) => ({
        ...current,
        favoriteIds: new Set((persisted as { favoriteIds: string[] })?.favoriteIds || []),
      }),
    }
  )
);
